import { CancellationReason, CarStatus, ListingHoldReason } from '../../__generated__/graphql';

/**
 * Spec 015: words for unlisted cars, recall holds and the admin relist. Shared
 * by the host car page, the host car list and the admin "Unlisted cars" page.
 */

export const NHTSA_VIN_LOOKUP_URL = 'https://www.nhtsa.gov/recalls';

export const CAR_NOT_BOOKABLE = 'CAR_NOT_BOOKABLE';
export const CAR_RELIST_REQUIRES_ADMIN = 'CAR_RELIST_REQUIRES_ADMIN';
export const RECALL_RELIST_INCOMPLETE = 'RECALL_RELIST_INCOMPLETE';

export interface HoldLike {
  reason: ListingHoldReason;
  pendingManualCheck?: boolean | null;
  campaigns?: ReadonlyArray<{ campaignNumber: string }> | null;
}

/** Short label for a hold, for badges and table cells. */
export function holdReasonLabel(reason: ListingHoldReason, brandName: string): string {
  switch (reason) {
    case ListingHoldReason.OpenRecall:
      return 'Safety recall';
    case ListingHoldReason.Admin:
      return `Unlisted by ${brandName}`;
    case ListingHoldReason.Sold:
      return 'Sold';
    case ListingHoldReason.Host:
    default:
      return 'Unlisted by host';
  }
}

/** The host car list badge, or null for a listed car. */
export function hostCarBadge(
  status: CarStatus | string | null | undefined,
  reason: ListingHoldReason | null | undefined,
  brandName: string,
): string | null {
  if (reason === ListingHoldReason.OpenRecall) return 'Unlisted: safety recall';
  if (reason === ListingHoldReason.Admin) return `Unlisted by ${brandName}`;
  if (reason === ListingHoldReason.Sold || status === CarStatus.Sold) return 'Sold';
  if (reason === ListingHoldReason.Host || status === CarStatus.Deactivated) return 'Unlisted';
  return null;
}

export interface HoldBannerCopy {
  title: string;
  body: string;
  /** "Email your evidence ..." for a recall hold seen by the host. */
  evidenceLine: string | null;
}

/** What the host (or an admin) reads above the car page sidebar. */
export function holdBannerCopy(
  hold: HoldLike,
  options: { brandName: string; supportEmail?: string | null; isAdminView: boolean },
): HoldBannerCopy {
  const contact = options.supportEmail?.trim() || options.brandName;
  switch (hold.reason) {
    case ListingHoldReason.OpenRecall: {
      const evidenceLine = options.isAdminView
        ? null
        : `Email your evidence (a screenshot of the NHTSA VIN lookup at nhtsa.gov/recalls, or a dealer repair order) to ${contact}. ${options.brandName} relists the car once it is checked.`;
      if (hold.pendingManualCheck && !(hold.campaigns?.length ?? 0)) {
        return {
          title: 'Unlisted until a recall check',
          body: `We could not check this car's make, model and year against NHTSA's recall data automatically, so ${options.brandName} checks it by hand before it can be booked.`,
          evidenceLine,
        };
      }
      return {
        title: 'Unlisted: safety recall',
        body: 'NHTSA lists a safety recall for this make, model and year. Guests cannot find or book the car until an admin confirms the recall is repaired or does not apply to it.',
        evidenceLine,
      };
    }
    case ListingHoldReason.Admin:
      return {
        title: `Unlisted by ${options.brandName}`,
        body: options.isAdminView
          ? 'An admin unlisted this car. Only an admin can relist it.'
          : `${options.brandName} unlisted this car. Contact ${contact} to relist it.`,
        evidenceLine: null,
      };
    case ListingHoldReason.Sold:
      return {
        title: 'Marked as sold',
        body: 'The car is hidden from search and cannot be booked.',
        evidenceLine: null,
      };
    case ListingHoldReason.Host:
    default:
      return {
        title: 'Unlisted',
        body: 'Your car is hidden from search results and cannot be booked.',
        evidenceLine: null,
      };
  }
}

/** Why the relist dialog cannot submit yet, or null when it can. */
export function relistFormProblem(input: {
  hold: HoldLike;
  checkedCampaigns: readonly string[];
  evidenceCount: number;
  uploading?: boolean;
}): string | null {
  if (input.uploading) return 'Wait for the upload to finish.';
  if (input.hold.reason !== ListingHoldReason.OpenRecall) return null;
  const missing = (input.hold.campaigns ?? [])
    .map(campaign => campaign.campaignNumber)
    .filter(number => !input.checkedCampaigns.includes(number));
  if (missing.length > 0) {
    return `Confirm ${missing.length === 1 ? 'recall' : 'recalls'} ${missing.join(', ')}.`;
  }
  if (input.evidenceCount === 0) {
    return 'Upload a screenshot of the NHTSA VIN lookup or a repair order.';
  }
  return null;
}

/** The GraphQL error code of an Apollo error, if any. */
export function graphqlErrorCode(error: unknown): string | null {
  const code = (error as { graphQLErrors?: Array<{ extensions?: { code?: unknown } }> } | null)
    ?.graphQLErrors?.[0]?.extensions?.code;
  return typeof code === 'string' ? code : null;
}

/** The server's message for an Apollo error, else the fallback. */
export function graphqlErrorMessage(error: unknown, fallback: string): string {
  const message = (
    error as { graphQLErrors?: Array<{ message?: string }> } | null
  )?.graphQLErrors?.[0]?.message?.trim();
  return message || fallback;
}

/** Trip page line for a trip cancelled because the car was unlisted. */
export function unlistedCancellationText(
  reason: CancellationReason | string | null | undefined,
  audience: 'guest' | 'host',
): string | null {
  if (reason === CancellationReason.SafetyRecall) {
    return audience === 'guest'
      ? "Cancelled because the car has an open safety recall. You've been refunded in full."
      : 'Cancelled because the car was unlisted for a safety recall. The guest was refunded in full.';
  }
  return null;
}

export function unmatchedReasonText(reason: string): string {
  switch (reason) {
    case 'missing_make_model_year':
      return 'Make, model or year missing';
    case 'model_not_in_nhtsa':
      return 'Model not in NHTSA data';
    case 'lookup_failed':
      return 'NHTSA lookup failing';
    default:
      return reason;
  }
}

export function recallJobModeText(mode: string): string {
  switch (mode) {
    case 'live':
      return 'Live';
    case 'dry-run':
      return 'Dry run (reports only)';
    default:
      return 'Off';
  }
}

/** yyyy-MM-dd of a local date, for the recall check date field. */
export function localDayKey(date: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export const RECALL_EVIDENCE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
export const RECALL_EVIDENCE_MAX_BYTES = 10 * 1024 * 1024;

/** Why a file cannot be uploaded as evidence, or null. */
export function evidenceFileProblem(file: { type: string; size: number }): string | null {
  if (!RECALL_EVIDENCE_TYPES.includes(file.type)) return 'Use a JPEG, PNG, WebP or PDF file.';
  if (file.size > RECALL_EVIDENCE_MAX_BYTES) return 'The file is larger than 10 MB.';
  if (file.size === 0) return 'The file is empty.';
  return null;
}

/** Spec 015 US-4: the add-car finish message when the new car waits for a recall check. */
export function addCarRecallNotice(options: {
  brandName: string;
  supportEmail?: string | null;
  pendingManualCheck: boolean;
}): string {
  const contact = options.supportEmail?.trim() || options.brandName;
  const why = options.pendingManualCheck
    ? `We could not check this make, model and year against NHTSA's recall data, so ${options.brandName} checks your car before it goes live.`
    : `This model year has safety recalls on record, so ${options.brandName} checks your car before it goes live.`;
  return `${why} Email a screenshot of the NHTSA VIN lookup (nhtsa.gov/recalls) to ${contact}.`;
}

export interface TripLike {
  status: string;
  startAt: number;
  endAt: number;
  hostFinishedCheckin?: boolean | null;
  guestFinishedCheckin?: boolean | null;
  hostFinishedCheckout?: boolean | null;
}

export type UnlistedTripAction = 'cancel' | 'endEarly' | null;

/**
 * Spec 015 US-3, US-8: what an admin can do with one trip of an unlisted car.
 * Future trips (not started, nobody checked in) are cancelled with a full
 * refund; a trip in progress on a recall-unlisted car can be ended early.
 */
export function unlistedTripAction(
  trip: TripLike,
  holdReason: ListingHoldReason,
  now: number,
): UnlistedTripAction {
  if (trip.status !== 'BOOKED' && trip.status !== 'SHORTENED') return null;
  if (trip.hostFinishedCheckout || trip.endAt <= now) return null;
  const started = trip.startAt <= now || trip.hostFinishedCheckin || trip.guestFinishedCheckin;
  if (!started) return 'cancel';
  return holdReason === ListingHoldReason.OpenRecall ? 'endEarly' : null;
}

/** Open trips first by start time; past and cancelled trips are left out. */
export function openTripsOf<T extends TripLike>(trips: readonly T[], now: number): T[] {
  return trips
    .filter(
      trip =>
        (trip.status === 'BOOKED' || trip.status === 'SHORTENED') &&
        !trip.hostFinishedCheckout &&
        trip.endAt > now,
    )
    .sort((a, b) => a.startAt - b.startAt);
}
